import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  PhoneCall, MapPin, CalendarDays, Clock, CheckCircle2, Loader2, ArrowLeft,
  ShieldCheck, XCircle, Sparkles, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { suggestEmailDomain } from '../lib/emailTypoSuggest';
import { getAttributionForConversion } from '../lib/adAttribution';
import { metaLead } from '../lib/metaPixel';
import { fireConversion } from '../lib/gtagConversions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRAND_ORANGE = '#FF7816';
const BRAND_GREEN = '#27B84A';
const BRAND_GREEN_DARK = '#118A35';

const fmtUsd = (n) => `$${Number(n).toFixed(2)}`;
const to12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

function BrandHeader() {
  return (
    <header className="border-b border-[#E4E8E6] bg-white">
      <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#18202D] font-bold" data-testid="book-brand-home">
          <ArrowLeft className="w-4 h-4 text-slate-500" />
          <img src="/cb-logo.png" alt="CountBucks" className="h-8 w-8" />
          <span className="text-lg tracking-tight">
            <span style={{ color: BRAND_ORANGE }}>Count</span>
            <span style={{ color: BRAND_GREEN_DARK }}>Bucks</span>
          </span>
        </Link>
        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4" style={{ color: BRAND_GREEN }} />
          Secure payment via Stripe
        </div>
      </div>
    </header>
  );
}

function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [appt, setAppt] = useState(null);
  const [tries, setTries] = useState(0);
  const [fixEmailValue, setFixEmailValue] = useState('');
  const [fixEmailSuggestion, setFixEmailSuggestion] = useState(null);
  const [fixBusy, setFixBusy] = useState(false);
  const [fixed, setFixed] = useState(false);
  const conversionFiredRef = useRef(false);

  useEffect(() => {
    const terminal = appt && ['confirmed', 'cancelled', 'expired'].includes(appt.status);
    if (!sessionId || terminal || tries > 10) return;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/appointments/session/${sessionId}`);
        if (r.ok) setAppt(await r.json());
      } catch { /* retry */ }
      setTries((n) => n + 1);
    }, tries === 0 ? 300 : 2000);
    return () => clearTimeout(t);
  }, [sessionId, appt, tries]);

  // Fire the Google Ads booking conversion exactly once — when Stripe
  // confirms the payment and the appointment becomes `confirmed`. We use
  // the Stripe session_id as the dedupe transaction_id.
  useEffect(() => {
    if (conversionFiredRef.current) return;
    if (appt?.status !== 'confirmed') return;
    conversionFiredRef.current = true;
    fireConversion('booking', {
      transactionId: sessionId || appt.appointment_id,
      value: (typeof appt.amount === 'number' ? appt.amount : undefined),
    });
    // Paid Stripe bookings are also our checkout conversion — Eli asked
    // for both signals; if only booking-label exists, checkout no-ops.
    if (appt.amount) {
      fireConversion('checkout', {
        transactionId: sessionId || appt.appointment_id,
        value: appt.amount,
      });
    }
  }, [appt, sessionId]);

  const submitFixedEmail = async () => {
    if (!/\S+@\S+\.\S+/.test(fixEmailValue.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setFixBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/appointments/session/${sessionId}/fix-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fixEmailValue.trim() }),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || 'Could not update email'); return; }
      toast.success('Confirmation resent — check your inbox');
      setFixed(true);
      // Re-fetch so the banner disappears
      const rr = await fetch(`${API_URL}/api/public/appointments/session/${sessionId}`);
      if (rr.ok) setAppt(await rr.json());
    } catch { toast.error('Could not update email'); }
    finally { setFixBusy(false); }
  };

  const confirmed = appt?.status === 'confirmed';
  const wasCancelled = appt && ['cancelled', 'expired'].includes(appt.status);
  const isInPerson = appt?.meeting_type === 'in_person';
  const showBounceFix = confirmed && appt?.email_bounced && !fixed;
  return (
    <div className="max-w-lg mx-auto text-center py-20 px-4" data-testid="booking-success">
      {confirmed ? (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: BRAND_GREEN }} />
          <h1 className="text-3xl font-bold text-[#18202D] mb-3">You&apos;re booked!</h1>
          <p className="text-slate-600 mb-6">
            {appt.name}, your <strong className="text-[#18202D]">{appt.duration_minutes}-minute</strong>{' '}
            {isInPerson ? 'in-person meeting' : 'call'} with our internal CPA Advisor is confirmed for{' '}
            <strong className="text-[#18202D]">
              {new Date(`${appt.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}{' '}
              at {to12h(appt.start_time)} ET
            </strong>.{' '}
            {isInPerson
              ? <>Please visit us at <strong className="text-[#18202D]">{appt.location}</strong>.</>
              : "We'll call you at the number you provided."}{' '}
            A confirmation email with a calendar invite is on its way.
          </p>
          {appt.manage_url && (
            <p className="text-sm text-slate-500 mb-2">
              Need a different time?{' '}
              <a href={appt.manage_url} className="font-semibold hover:underline"
                 style={{ color: BRAND_GREEN_DARK }} data-testid="success-manage-link">
                Reschedule or cancel
              </a>{' '}
              — full refund if you cancel 48+ hours ahead.
            </p>
          )}

          {showBounceFix && (
            <div
              className="mt-8 rounded-2xl border-2 p-5 text-left"
              style={{ backgroundColor: '#FFF7ED', borderColor: BRAND_ORANGE }}
              data-testid="bounce-fix-card"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: BRAND_ORANGE }}>
                  <XCircle className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-[#18202D]">Your confirmation email couldn&apos;t be delivered</p>
                  <p className="text-sm text-slate-700 mt-0.5">
                    We tried to email your calendar invite to{' '}
                    <strong>{appt.email || 'the address you entered'}</strong> but it bounced.
                    Enter your correct email below and we&apos;ll re-send everything.
                  </p>
                </div>
              </div>
              <Input
                type="email"
                value={fixEmailValue}
                onChange={(e) => { setFixEmailValue(e.target.value); setFixEmailSuggestion(null); }}
                onBlur={(e) => setFixEmailSuggestion(suggestEmailDomain(e.target.value))}
                placeholder="you@yourbusiness.com"
                className="bg-white border-[#E4E8E6] text-[#18202D]"
                data-testid="bounce-fix-email-input"
              />
              {fixEmailSuggestion && (
                <button
                  type="button"
                  onClick={() => { setFixEmailValue(fixEmailSuggestion); setFixEmailSuggestion(null); }}
                  className="mt-1.5 w-full text-left rounded-lg border px-2.5 py-1.5 text-xs"
                  style={{ backgroundColor: '#fff', borderColor: BRAND_ORANGE, color: '#7C3A00' }}
                  data-testid="bounce-fix-typo-suggestion"
                >
                  Did you mean <strong>{fixEmailSuggestion}</strong>?
                </button>
              )}
              <Button
                onClick={submitFixedEmail}
                disabled={!fixEmailValue.trim() || fixBusy}
                className="w-full mt-3 text-white font-bold h-11 hover:opacity-90"
                style={{ backgroundColor: BRAND_GREEN }}
                data-testid="bounce-fix-submit-btn"
              >
                {fixBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update email & re-send confirmation'}
              </Button>
              <p className="text-[11px] text-slate-500 mt-2 text-center">
                Your appointment is still confirmed — we just need a working email to send the calendar invite.
              </p>
            </div>
          )}

          {fixed && (
            <div className="mt-6 rounded-xl border p-4 text-left"
                 style={{ backgroundColor: '#F2FBF4', borderColor: BRAND_GREEN }}
                 data-testid="bounce-fix-success-card">
              <p className="text-sm font-semibold flex items-center gap-2" style={{ color: BRAND_GREEN_DARK }}>
                <CheckCircle2 className="w-4 h-4" /> Confirmation re-sent
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Check your inbox in the next minute. If it still doesn&apos;t arrive, look in spam or reply
                to this ticket for help.
              </p>
            </div>
          )}
        </>
      ) : wasCancelled ? (
        <>
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-[#18202D] mb-3" data-testid="booking-cancelled-title">
            This appointment was cancelled
          </h1>
          <p className="text-slate-600">
            {appt.status === 'expired'
              ? 'The payment window expired before this booking was completed. Pick a new time to rebook.'
              : 'If you were charged, your refund will be processed. Need a new time? Book again below.'}
          </p>
          <Button asChild className="mt-8 text-white" style={{ backgroundColor: BRAND_GREEN }}>
            <Link to="/book">Book a new time</Link>
          </Button>
        </>
      ) : tries > 10 ? (
        <>
          <Clock className="w-12 h-12 mx-auto mb-6" style={{ color: BRAND_ORANGE }} />
          <h1 className="text-2xl font-bold text-[#18202D] mb-3">Still confirming…</h1>
          <p className="text-slate-600">
            Your payment is processing. If your card was charged, a confirmation email will arrive
            shortly — no need to rebook.
          </p>
        </>
      ) : (
        <>
          <Loader2 className="w-12 h-12 mx-auto mb-6 animate-spin" style={{ color: BRAND_GREEN }} />
          <h1 className="text-2xl font-bold text-[#18202D] mb-3">Confirming your payment…</h1>
          <p className="text-slate-600">This usually takes a few seconds.</p>
        </>
      )}
      <Button asChild variant="outline" className="mt-8 border-[#E4E8E6] text-[#18202D] hover:bg-[#F7F9F8]">
        <Link to="/">Back to CountBucks</Link>
      </Button>
    </div>
  );
}

export default function BookAppointment() {
  const location = useLocation();
  const isSuccess = location.pathname.endsWith('/success');

  const [config, setConfig] = useState(null);
  const [meetingType, setMeetingType] = useState('phone'); // 'phone' | 'in_person'
  const [locationId, setLocationId] = useState(null);
  const [duration, setDuration] = useState(30);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [slotsRetry, setSlotsRetry] = useState(0);
  const [slotsClosed, setSlotsClosed] = useState(false);
  const [credits, setCredits] = useState(null);
  const [useCredits, setUseCredits] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [emailMx, setEmailMx] = useState(null); // null | 'checking' | 'ok' | 'no_mx' | 'unknown'
  const [emailMxMessage, setEmailMxMessage] = useState('');

  const checkEmailMx = async (value) => {
    const v = (value || '').trim();
    if (!/\S+@\S+\.\S+/.test(v)) { setEmailMx(null); return; }
    setEmailMx('checking');
    try {
      const r = await fetch(`${API_URL}/api/public/appointments/validate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: v }),
      });
      const j = r.ok ? await r.json() : null;
      setEmailMx(j?.result || 'unknown');
      setEmailMxMessage(j?.message || '');
    } catch {
      setEmailMx('unknown');
    }
  };

  const { minDate, maxDate } = useMemo(() => {
    const min = new Date(Date.now() + 24 * 3600 * 1000);
    const max = new Date(Date.now() + 30 * 24 * 3600 * 1000);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { minDate: iso(min), maxDate: iso(max) };
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/public/appointments/config`)
      .then((r) => r.json()).then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/appointments/my-credits`, { credentials: 'include' })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled || !j) return;
        setCredits(j);
        if (j.eligible && j.total_minutes > 0) setUseCredits(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSlot('');
    if (!date) { setSlots(null); return; }
    setSlots(null);
    setSlotsClosed(false);
    // In-person availability is scoped to the chosen office's weekly hours.
    const locId = locationId
      || (config?.office_locations || []).find((l) => l.is_default)?.location_id
      || config?.office?.location_id;
    const locParam = meetingType === 'in_person' && locId
      ? `&meeting_type=in_person&location_id=${locId}`
      : '';
    fetch(`${API_URL}/api/public/appointments/availability?date=${date}&duration=${duration}${locParam}`)
      .then(async (r) => {
        const j = await r.json();
        if (!r.ok) { toast.error(j.detail || 'Could not load availability'); setSlots('error'); return; }
        setSlotsClosed(!!j.location_closed);
        setSlots(j.slots);
      })
      .catch(() => setSlots('error'));
  }, [date, duration, slotsRetry, meetingType, locationId, config]);

  const price = config?.durations?.find((d) => d.minutes === duration)?.price ?? (duration / 60) * 150;

  // Individual validation flags so we can render specific hints when the
  // Pay button is disabled — the previous "silent disable" behavior left
  // users staring at a grey button with no idea what was missing.
  const nameValid = form.name.trim().length >= 2;
  const emailValid = /\S+@\S+\.\S+/.test(form.email) && emailMx !== 'no_mx';
  const phoneValid = form.phone.trim().length >= 7;
  const notesValid = form.notes.trim().length >= 1;
  const formValid = nameValid && emailValid && phoneValid && notesValid;

  const missingFields = [];
  if (!nameValid) missingFields.push('your name');
  if (!emailValid) missingFields.push(emailMx === 'no_mx' ? 'an email address we can deliver to' : 'a valid email');
  if (!phoneValid) missingFields.push('a phone number');
  if (!notesValid) missingFields.push('what you\'d like to discuss');
  if (!date) missingFields.push('a date');
  if (date && !slot) missingFields.push('a time slot');

  const creditsAvailable = credits?.eligible && credits.total_minutes > 0;
  const enoughCredits = credits?.total_minutes >= duration;
  const activeCreditBooking = useCredits && creditsAvailable && enoughCredits;
  const locations = config?.office_locations || (config?.office ? [config.office] : []);
  const defaultLoc = locations.find((l) => l.is_default) || locations[0] || null;
  const selectedLocation = locations.find((l) => l.location_id === locationId) || defaultLoc;
  const isInPerson = meetingType === 'in_person';

  // Auto-select the default location when the user first switches to
  // in-person, so a single-location shop keeps working invisibly.
  useEffect(() => {
    if (isInPerson && !locationId && defaultLoc) setLocationId(defaultLoc.location_id);
  }, [isInPerson, locationId, defaultLoc]);

  const handlePay = async () => {
    if (!formValid || !slot || !date) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/appointments/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          notes: form.notes.trim(),
          date,
          start_time: slot,
          duration_minutes: duration,
          origin_url: window.location.origin,
          use_credits: activeCreditBooking,
          meeting_type: meetingType,
          location_id: isInPerson ? (locationId || defaultLoc?.location_id) : null,
          attribution: getAttributionForConversion(),
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Could not start booking');
        if (r.status === 409) {
          setSlot('');
          const locId2 = isInPerson ? (locationId || defaultLoc?.location_id) : null;
          const lp = locId2 ? `&meeting_type=in_person&location_id=${locId2}` : '';
          const rr = await fetch(`${API_URL}/api/public/appointments/availability?date=${date}&duration=${duration}${lp}`);
          if (rr.ok) {
            const jj = await rr.json();
            setSlotsClosed(!!jj.location_closed);
            setSlots(jj.slots);
          }
        }
        return;
      }
      // Meta Lead — the visitor submitted the public booking form (a demo /
      // contact lead). Browser-only Pixel event; no PII sent here.
      metaLead({ content_name: 'Appointment Booking' });
      if (j.paid_with_credits) {
        toast.success(`Confirmed! ${duration} minutes deducted from your booking credits.`);
        // Credits-paid → conversion is complete right here. For Stripe
        // checkouts the conversion fires on the /book/success page once
        // the appointment status becomes "confirmed".
        fireConversion('booking', {
          transactionId: j.appointment_id,
          value: 0,
        });
        window.location.href = j.manage_url || '/book/success';
        return;
      }
      window.location.href = j.url;
    } catch {
      toast.error('Could not start booking — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabel = (n) => (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold mr-2"
          style={{ backgroundColor: BRAND_ORANGE }}>{n}</span>
  );

  return (
    <div className="min-h-screen bg-white text-[#18202D]">
      <BrandHeader />

      {isSuccess ? (
        <BookingSuccess />
      ) : (
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                 style={{ backgroundColor: '#F2FBF4', color: BRAND_GREEN_DARK, border: `1px solid ${BRAND_GREEN}40` }}>
              <PhoneCall className="w-3.5 h-3.5" /> Internal CPA Advisor
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">
              <span style={{ color: BRAND_ORANGE }}>Book</span>{' '}
              <span className="text-[#18202D]">a Call or Meeting</span>
            </h1>
            <p className="text-slate-600 max-w-xl mx-auto">
              $150/hour, billed in 15-minute increments. Monday–Friday, 9:00 AM – 4:00 PM Eastern.
              Pre-paid — your slot locks in the moment you pay.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left: meeting type + duration + date + slots */}
            <div className="lg:col-span-3 space-y-8">
              {/* Step 1 — Meeting Type */}
              <section>
                <h2 className="text-sm font-bold text-[#18202D] uppercase tracking-wider mb-3 flex items-center">
                  {stepLabel(1)} How would you like to meet?
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="meeting-type-grid">
                  {[
                    { id: 'phone', icon: PhoneCall, label: 'Phone Call',
                      blurb: "We'll call you at the number you provide." },
                    { id: 'in_person', icon: MapPin, label: 'In-Person Meeting',
                      blurb: defaultLoc ? `Visit us at ${defaultLoc.city}, ${defaultLoc.state}.` : 'Visit our office.' },
                  ].map(({ id, icon: Icon, label, blurb }) => {
                    const selected = meetingType === id;
                    return (
                      <button
                        key={id}
                        onClick={() => setMeetingType(id)}
                        data-testid={`meeting-type-${id}`}
                        className={`text-left rounded-2xl p-4 border-2 transition-all bg-white ${
                          selected
                            ? 'shadow-md'
                            : 'border-[#E4E8E6] hover:border-[#27B84A]/60'
                        }`}
                        style={selected ? { borderColor: BRAND_GREEN, backgroundColor: '#F2FBF4' } : {}}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center"
                               style={{ backgroundColor: selected ? BRAND_GREEN : '#F7F9F8' }}>
                            <Icon className="w-5 h-5" style={{ color: selected ? '#fff' : BRAND_GREEN_DARK }} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-[#18202D]">{label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{blurb}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {isInPerson && locations.length > 1 && (
                  <div className="mt-3" data-testid="location-picker">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                      Choose a location
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {locations.map((loc) => {
                        const sel = (selectedLocation?.location_id === loc.location_id);
                        return (
                          <button
                            key={loc.location_id}
                            onClick={() => setLocationId(loc.location_id)}
                            data-testid={`location-${loc.location_id}`}
                            className={`text-left rounded-xl p-3 border-2 transition-all bg-white ${
                              sel ? '' : 'border-[#E4E8E6] hover:border-[#27B84A]/60'
                            }`}
                            style={sel ? { borderColor: BRAND_ORANGE, backgroundColor: '#FFF7ED' } : {}}
                          >
                            <p className="text-sm font-bold text-[#18202D]">{loc.name}</p>
                            <p className="text-xs text-slate-600 mt-0.5">
                              {loc.line1}{loc.line2 ? `, ${loc.line2}` : ''}<br/>
                              {loc.city}, {loc.state} {loc.postal}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isInPerson && selectedLocation && (
                  <div className="mt-3 rounded-xl border border-[#E4E8E6] bg-[#F7F9F8] p-4"
                       data-testid="office-address-card">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND_ORANGE }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          {locations.length > 1 ? 'Selected location' : 'Our office'}
                        </p>
                        <p className="text-sm font-semibold text-[#18202D] mt-1">
                          {selectedLocation.name && <>{selectedLocation.name}<br/></>}
                          {selectedLocation.line1}
                          {selectedLocation.line2 ? <><br/>{selectedLocation.line2}</> : null}<br/>
                          {selectedLocation.city}, {selectedLocation.state} {selectedLocation.postal}
                        </p>
                        <a href={selectedLocation.map_url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 mt-2 text-xs font-semibold hover:underline"
                           style={{ color: BRAND_GREEN_DARK }}
                           data-testid="office-map-link">
                          Open in Google Maps <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* Step 2 — Duration */}
              <section>
                <h2 className="text-sm font-bold text-[#18202D] uppercase tracking-wider mb-3 flex items-center">
                  {stepLabel(2)} <Clock className="w-4 h-4 mr-1.5" style={{ color: BRAND_GREEN_DARK }} /> Choose a duration
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(config?.durations || [15, 30, 45, 60].map((m) => ({ minutes: m, price: (m / 60) * 150 }))).map((d) => {
                    const selected = duration === d.minutes;
                    return (
                      <button
                        key={d.minutes}
                        onClick={() => setDuration(d.minutes)}
                        data-testid={`duration-${d.minutes}`}
                        className={`rounded-xl p-4 text-center border-2 transition-all bg-white ${
                          selected ? 'shadow-md' : 'border-[#E4E8E6] hover:border-[#27B84A]/60'
                        }`}
                        style={selected ? { borderColor: BRAND_GREEN, backgroundColor: '#F2FBF4' } : {}}
                      >
                        <p className="text-xl font-bold text-[#18202D]">{d.minutes} min</p>
                        <p className={`text-sm mt-1 font-semibold ${selected ? '' : 'text-slate-500'}`}
                           style={selected ? { color: BRAND_GREEN_DARK } : {}}>
                          {fmtUsd(d.price)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Step 3 — Date */}
              <section>
                <h2 className="text-sm font-bold text-[#18202D] uppercase tracking-wider mb-3 flex items-center">
                  {stepLabel(3)} <CalendarDays className="w-4 h-4 mr-1.5" style={{ color: BRAND_GREEN_DARK }} /> Pick a date (Mon–Fri)
                </h2>
                <Input
                  type="date"
                  value={date}
                  min={minDate}
                  max={maxDate}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-white border-[#E4E8E6] text-[#18202D] max-w-xs"
                  data-testid="booking-date-input"
                />
                <p className="text-xs text-slate-500 mt-2">
                  At least 24 hours&apos; notice · up to 30 days ahead · times shown in Eastern Time
                </p>
              </section>

              {/* Step 4 — Time */}
              {date && (
                <section>
                  <h2 className="text-sm font-bold text-[#18202D] uppercase tracking-wider mb-3 flex items-center">
                    {stepLabel(4)} Pick a time (ET)
                  </h2>
                  {slots === null ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading open slots…
                    </div>
                  ) : slots === 'error' ? (
                    <div data-testid="slots-error-message">
                      <p className="text-sm text-red-600 mb-2">
                        We could not load the open times — please check your connection and try again.
                      </p>
                      <Button variant="outline" size="sm"
                              onClick={() => setSlotsRetry((n) => n + 1)}
                              className="border-[#E4E8E6] text-[#18202D]"
                              data-testid="slots-retry-btn">
                        Retry
                      </Button>
                    </div>
                  ) : slots.length === 0 ? (
                    slotsClosed ? (
                      <p className="text-sm font-semibold" style={{ color: BRAND_ORANGE }} data-testid="location-closed-message">
                        {selectedLocation?.name || 'The office'} is closed that day — pick another date, or switch to a phone call.
                      </p>
                    ) : (
                      <p className="text-sm font-semibold" style={{ color: BRAND_ORANGE }} data-testid="no-slots-message">
                        No open slots that day — weekends aren&apos;t available, and appointments need 24h notice. Try another weekday.
                      </p>
                    )
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="slot-grid">
                      {slots.map((s) => {
                        const selected = slot === s;
                        return (
                          <button
                            key={s}
                            onClick={() => setSlot(s)}
                            data-testid={`slot-${s}`}
                            className={`rounded-lg px-2 py-2.5 text-sm font-semibold border-2 transition-all ${
                              selected
                                ? 'text-white shadow'
                                : 'bg-white border-[#E4E8E6] text-[#18202D] hover:border-[#27B84A]'
                            }`}
                            style={selected ? { backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN } : {}}
                          >
                            {to12h(s)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
            </div>

            {/* Right: form + summary */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl bg-white border border-[#E4E8E6] p-6 sticky top-6 shadow-sm">
                <h2 className="text-sm font-bold text-[#18202D] uppercase tracking-wider mb-4 flex items-center">
                  {stepLabel(5)} Your details
                </h2>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Full name *</label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Smith"
                      className="bg-white border-[#E4E8E6] text-[#18202D] mt-1"
                      data-testid="booking-name" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Email *</label>
                    <Input type="email" value={form.email}
                      onChange={(e) => { setForm({ ...form, email: e.target.value }); setEmailSuggestion(null); setEmailMx(null); }}
                      onBlur={(e) => {
                        const s = suggestEmailDomain(e.target.value);
                        setEmailSuggestion(s);
                        if (!s) checkEmailMx(e.target.value);
                      }}
                      placeholder="jane@business.com"
                      className={`bg-white text-[#18202D] mt-1 ${emailMx === 'no_mx' ? 'border-red-400 focus-visible:ring-red-300' : 'border-[#E4E8E6]'}`}
                      aria-invalid={emailMx === 'no_mx' || undefined}
                      data-testid="booking-email" />
                    {emailMx === 'checking' && (
                      <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1" data-testid="email-mx-checking">
                        <Loader2 className="w-3 h-3 animate-spin" /> Checking that this address can receive mail…
                      </p>
                    )}
                    {emailMx === 'no_mx' && !emailSuggestion && (
                      <div
                        className="mt-1.5 flex items-start gap-2 rounded-lg border px-2.5 py-2 text-xs"
                        style={{ backgroundColor: '#FEF2F2', borderColor: '#F87171', color: '#991B1B' }}
                        role="alert"
                        data-testid="email-mx-warning"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-red-500" />
                        <span>{emailMxMessage || 'We couldn\u2019t find a mail server for that domain — please double-check your email.'}</span>
                      </div>
                    )}
                    {emailMx === 'ok' && !emailSuggestion && (
                      <p className="mt-1 text-[11px] text-emerald-700" data-testid="email-mx-ok">Looks good — this address can receive mail.</p>
                    )}
                    {emailSuggestion && (
                      <button
                        type="button"
                        onClick={() => { const fixedEmail = emailSuggestion; setForm((f) => ({ ...f, email: fixedEmail })); setEmailSuggestion(null); checkEmailMx(fixedEmail); }}
                        className="mt-1.5 w-full text-left flex items-start gap-2 rounded-lg border px-2.5 py-2 text-xs hover:opacity-90"
                        style={{ backgroundColor: '#FFF7ED', borderColor: BRAND_ORANGE, color: '#7C3A00' }}
                        data-testid="email-typo-suggestion"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: BRAND_ORANGE }} />
                        <span>
                          Did you mean <strong>{emailSuggestion}</strong>?
                          <span className="block text-[10px] opacity-75 mt-0.5">
                            Click to use this — a typo means we can&apos;t email your confirmation.
                          </span>
                        </span>
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">
                      {isInPerson ? 'Best phone number (in case we need to reach you) *' : 'Phone number to call *'}
                    </label>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="bg-white border-[#E4E8E6] text-[#18202D] mt-1"
                      data-testid="booking-phone" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">What would you like to discuss? *</label>
                    <Textarea
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={3}
                      placeholder="Anything you want! Taxes, bookkeeping, cash flow, entity setup, growth plans — just tell us what you'd like to cover so your advisor comes prepared."
                      className="bg-white border-[#E4E8E6] text-[#18202D] mt-1 placeholder:text-slate-400"
                      data-testid="booking-notes"
                    />
                  </div>
                </div>

                <div className="border-t border-[#E4E8E6] mt-5 pt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between text-slate-500">
                    <span>Type</span>
                    <span className="text-[#18202D] font-semibold" data-testid="summary-meeting-type">
                      {isInPerson ? 'In-Person Meeting' : 'Phone Call'}
                    </span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Duration</span>
                    <span className="text-[#18202D] font-semibold">{duration} minutes</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>When</span>
                    <span className="text-[#18202D] font-semibold">
                      {date && slot ? `${date} · ${to12h(slot)} ET` : '—'}
                    </span>
                  </div>
                  {isInPerson && selectedLocation && (
                    <div className="flex justify-between text-slate-500">
                      <span>Where</span>
                      <span className="text-[#18202D] font-semibold text-right">
                        {selectedLocation.name || `${selectedLocation.city}, ${selectedLocation.state}`}
                      </span>
                    </div>
                  )}

                  {creditsAvailable && (
                    <div
                      className="mt-4 rounded-xl p-3 border-2"
                      style={{
                        backgroundColor: activeCreditBooking ? '#FFF7ED' : '#F7F9F8',
                        borderColor: activeCreditBooking ? BRAND_ORANGE : '#E4E8E6',
                      }}
                      data-testid="credit-toggle-panel"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-[#18202D] flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND_ORANGE }} />
                            Use free credit
                          </p>
                          <p className="text-[11px] text-slate-600 mt-0.5">
                            {credits.total_minutes} min available on your{' '}
                            {credits.plan_id === 'done_for_you' ? 'Done For You' : 'Platinum'} plan
                          </p>
                        </div>
                        <Switch
                          checked={activeCreditBooking}
                          onCheckedChange={(v) => setUseCredits(!!v)}
                          disabled={!enoughCredits}
                          data-testid="use-credits-toggle"
                        />
                      </div>
                      {!enoughCredits && (
                        <p className="text-[11px] font-semibold mt-2" style={{ color: BRAND_ORANGE }}
                           data-testid="credits-insufficient-msg">
                          Not enough credits for a {duration}-minute {isInPerson ? 'meeting' : 'call'} — pick a shorter duration to apply it, or pay for this one.
                        </p>
                      )}
                      {activeCreditBooking && (
                        <p className="text-[11px] font-semibold mt-2" style={{ color: BRAND_GREEN_DARK }}>
                          {duration} minutes will be deducted from your monthly credit balance.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between text-base font-bold pt-3">
                    <span className="text-[#18202D]">Total due now</span>
                    <span data-testid="booking-total"
                          style={{ color: activeCreditBooking ? BRAND_GREEN_DARK : BRAND_GREEN_DARK }}>
                      {activeCreditBooking ? 'Free (credit)' : fmtUsd(price)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handlePay}
                  disabled={!formValid || !slot || !date || submitting}
                  className="w-full mt-5 text-white font-bold h-11 hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: activeCreditBooking ? BRAND_ORANGE : BRAND_GREEN }}
                  data-testid="booking-pay-btn"
                >
                  {submitting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : activeCreditBooking
                      ? `Confirm ${duration}-min ${isInPerson ? 'meeting' : 'call'} (free)`
                      : `Pay ${fmtUsd(price)} & schedule`}
                </Button>

                {missingFields.length > 0 && (
                  <p className="text-[11px] font-semibold mt-2 text-center"
                     style={{ color: BRAND_ORANGE }}
                     data-testid="booking-missing-hint">
                    Add {missingFields.slice(0, -1).join(', ')}
                    {missingFields.length > 1 ? ' and ' : ''}
                    {missingFields.slice(-1)} to continue.
                  </p>
                )}

                <p className="text-[11px] text-slate-500 mt-3 text-center">
                  {activeCreditBooking
                    ? `Confirmed instantly. Cancel 24+ hours ahead to get your credit back.`
                    : `Payment required to confirm. You'll get an email confirmation instantly after payment.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
